import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';
import React from 'react';
import { BackgroundGradient, Box, Card, Link, Typography } from '@drone-js/core';
import { BackgroundType } from '@drone-js/theme';
import { withQueryString } from 'core/service/Url/Url';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesTopDestinationCardClickEventActivitiesFieldsV1,
    ActivitiesTopDestinationCardSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesTopDestinationCardClickEventActivitiesManifestV1,
    ActivitiesTopDestinationCardSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { InView } from 'react-intersection-observer';
import { RecentSearchItem } from 'core/type/SuggestionResult.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { MAX_RECENT_SEARCH, setRecentlySearch } from 'component/common/TextSearch/suggestionUtils';
import { useSelector } from 'react-redux';
import { selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

interface Props {
    city: TopDestination;
    cid: number;
    isCompact?: boolean;
    index: number;
}

const CARD_WIDTH = 200;
const CARD_HEIGHT = 240;
const CARD_WIDTH_COMPACT = 140;
const CARD_HEIGHT_COMPACT = 140;

const gradientBackground: BackgroundGradient<BackgroundType> = {
    stops: [
        {
            backgroundColor: 'base.transparent',
        },
        {
            backgroundColor: 'base.overlay.strong',
            position: '80%',
        },
    ],
};

export const TopCitiesItem: React.FC<Props> = (props) => {
    const { city, isCompact, index } = props;
    const cardIndex = index + 1;
    const cardWidth = props.isCompact ? CARD_WIDTH_COMPACT : CARD_WIDTH;
    const cardHeight = props.isCompact ? CARD_HEIGHT_COMPACT : CARD_HEIGHT;
    const cityPageUrl = withQueryString(city.cityUrl, String(props.cid));
    const isSearchBoxFeatureGroup = useSelector(selectFeatureBehaviorFromBootstrap).SearchBoxFeatureGroup;

    const onCityClick = () => {
        trackElementEvent<ActivitiesTopDestinationCardClickEventActivitiesFieldsV1>(
            ActivitiesTopDestinationCardClickEventActivitiesManifestV1.ids,
            { action_element_index: cardIndex, action_element_value: city.cityId },
            { city_id: city.cityId },
        );

        const recentClick: RecentSearchItem = {
            id: city.cityId,
            type: TextSearchType.City,
            activityName: '',
            url: city.cityUrl,
            isUrlWithPrefix: true,
            cityName: city.cityName ?? city.cityNameEnglish,
            cityId: city.cityId,
            countryName: city.countryName || '',
            supplierId: undefined,
        };

        isSearchBoxFeatureGroup && setRecentlySearch(recentClick, MAX_RECENT_SEARCH);
    };

    const onSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesTopDestinationCardSeenEventActivitiesFieldsV1>(
            ActivitiesTopDestinationCardSeenEventActivitiesManifestV1.ids,
            { action_element_index: cardIndex, action_element_value: city.cityId.toString() },
            { city_id: city.cityId.toString() },
        );
    };

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            {/* eslint-disable-next-line @agoda/agoda-a11y/no-inaccessible-drone-link -- @agoda/agoda-a11y/no-inaccessible-drone-link[1dd85d05-e862-41c6-9346-361441f09e79] */}
            <Link
                data-testid="activities-top-destination-card"
                data-element-name="activities-top-destination-card"
                data-element-index={cardIndex}
                onClick={onCityClick}
                href={cityPageUrl}
            >
                <Card
                    data-testid="activities-top-destination-item"
                    type="interactive"
                    height={`${cardHeight}px`}
                    width={`${cardWidth}px`}
                    position="relative"
                    overflow="hidden"
                    me="12"
                    mt="4"
                >
                    <ActivityImage
                        src={city.imageUrl}
                        alt={city.cityName}
                        title={city.cityName}
                        borderRadius="8"
                        objectFit="cover"
                        data-testid="activities-top-destination-item-image"
                        height="100%"
                        width="100%"
                        responsive="original"
                        loading="immediate"
                    />
                    {!isCompact && (
                        <Box height="100px" position="absolute" bottom={0} width="100%" backgroundColor={gradientBackground}>
                            <Box position="absolute" bottom="12px" px="12">
                                <Typography tag="h3" variant="heading-default" aria-level={2} textColor="base.inverted" data-testid="activities-top-destination-item-title">
                                    {city.cityName}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Card>
                {isCompact && (
                    <Typography px="4" variant="body-s-strong" mt="8" me="12" aria-level={2} role="heading" data-testid="activities-top-destination-item-compact-title">
                        {city.cityName}
                    </Typography>
                )}
            </Link>
        </InView>
    );
};

TopCitiesItem.displayName = 'TopCitiesItem';
